/**
 * usePersonalRoutines / usePersonalRoutineView (story 027.007) — the person's saved Routine.v1
 * routines, read from Atlas through Command's one authoring client
 * (`GET /api/routine-authoring/routines?scope=…` and `/routines/u/:useId`).
 *
 * Both routines surfaces (the chat navigation tree and My routines) use THIS hook, so they show
 * the same authorised items, by the same IDs. An API failure is `unavailable`, which every
 * surface renders differently from `empty`. A response from an earlier request (or an earlier
 * sign-in) never replaces a newer one, and every result is stamped with the identity (user +
 * organisation) it was read for: a switch clears it at once. The list pages through Atlas's
 * `has_more` / `next_offset` (`loadMore`). A durable save in chat fires ROUTINES_CHANGED_EVENT and
 * both surfaces re-read. Nothing here writes, and nothing is cached across sessions.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWithAuth } from "../lib/api";
import { useCurrentUser } from "../providers/ClerkProvider";
import { useCurrentOrgId } from "../providers/OrgProvider";
import { ROUTINES_CHANGED_EVENT } from "../lib/routines-changed";

export type PersonalRoutineScope = "mine" | "shared" | "adopted";

export interface PersonalRoutineNeed {
  kind: "setting" | "connection";
  key?: string;
  slot?: string;
  operation?: string;
  domain?: string;
  reason: string;
}

export interface PersonalRoutineUnavailable {
  kind: "unavailable";
  reason: string;
}

export interface PersonalRoutineSummary {
  useId: string | null;
  routineId: string;
  name: string;
  owner: { principalId: string; displayName: string | null } | null;
  originKind: string;
  version: {
    id: string;
    number: string;
    contentHash: string;
    verificationStatus: string;
  };
  consentState: string | null;
  triggerKind: string | null;
  setupState: "complete" | "needs_setup" | "unavailable";
  needsSetup: PersonalRoutineNeed[] | PersonalRoutineUnavailable;
  cost: { kind: string };
  run: { enabled: boolean; reason: string | null };
}

export interface PersonalRoutineView extends PersonalRoutineSummary {
  useId: string;
  editable: boolean;
  changedSinceConsent: boolean;
  delegation: {
    revocation: string;
    grantState: string;
    grantExpiresAt: string | null;
    suspensionReasons: string[];
  };
  bindings: Array<{
    slot: string;
    operation: string;
    domain: string;
    provider: string | null;
    connectionId: string | null;
  }>;
  runtime: Record<string, PersonalRoutineUnavailable | number | boolean | string | null>;
}

export type PersonalRoutinesStatus =
  | "loading"
  | "ready"
  | "empty"
  | "unauthenticated"
  | "forbidden"
  | "unavailable";

export interface UsePersonalRoutinesResult {
  items: PersonalRoutineSummary[];
  status: PersonalRoutinesStatus;
  refetch: () => Promise<void>;
  /** Atlas has more routines after these (its `has_more`). */
  hasMore: boolean;
  /** Reads the next page at Atlas's `next_offset` and appends it. */
  loadMore: () => Promise<void>;
  loadingMore: boolean;
  /** The last next-page read failed; the routines already shown stay. */
  loadMoreFailed: boolean;
}

const LIST_URL = "/api/routine-authoring/routines";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function statusFor(httpStatus: number): PersonalRoutinesStatus {
  return httpStatus === 401 ? "unauthenticated" : httpStatus === 403 ? "forbidden" : "unavailable";
}

/**
 * Who the routines are read for: the signed-in user AND the active organisation. Every piece of
 * state below is stamped with it; a switch of either clears what is shown and re-reads, and an
 * answer to a request made for another identity is dropped.
 */
export function useRoutinesIdentity(): string {
  const userId = useCurrentUser().user?.id ?? "";
  const orgId = useCurrentOrgId();
  return JSON.stringify([userId, orgId]);
}

interface ListState {
  identity: string;
  items: PersonalRoutineSummary[];
  status: PersonalRoutinesStatus;
  nextOffset: number | null;
  loadingMore: boolean;
  loadMoreFailed: boolean;
}

function parsePage(
  status: number,
  body: unknown,
): { items: PersonalRoutineSummary[]; nextOffset: number | null } | null {
  if (status !== 200 || !isRecord(body) || body.result !== "listed" || !Array.isArray(body.items)) {
    return null;
  }
  const next =
    body.has_more === true && typeof body.next_offset === "number" && body.next_offset >= 0
      ? body.next_offset
      : null;
  return { items: body.items as PersonalRoutineSummary[], nextOffset: next };
}

function initialList(identity: string): ListState {
  return {
    identity,
    items: [],
    status: "loading",
    nextOffset: null,
    loadingMore: false,
    loadMoreFailed: false,
  };
}

export function usePersonalRoutines(
  scope: PersonalRoutineScope = "mine",
): UsePersonalRoutinesResult {
  const identity = useRoutinesIdentity();
  const [state, setState] = useState<ListState>(() => initialList(identity));
  const generation = useRef(0);
  const current = useRef(identity);
  current.current = identity;
  const nextOffset = useRef<number | null>(null);
  nextOffset.current = state.identity === identity ? state.nextOffset : null;

  const load = useCallback(
    async (quiet: boolean) => {
      const id = ++generation.current;
      const stale = () => id !== generation.current || current.current !== identity;
      if (!quiet) {
        setState(initialList(identity));
      }
      try {
        const res = await fetchWithAuth(`${LIST_URL}?scope=${encodeURIComponent(scope)}`);
        const body: unknown = await res.json().catch(() => null);
        if (stale()) {
          return;
        }
        const page = parsePage(res.status, body);
        if (!page) {
          setState({
            ...initialList(identity),
            status: res.status === 200 ? "unavailable" : statusFor(res.status),
          });
          return;
        }
        setState({
          ...initialList(identity),
          items: page.items,
          status: page.items.length === 0 ? "empty" : "ready",
          nextOffset: page.nextOffset,
        });
      } catch {
        if (stale()) {
          return;
        }
        setState({ ...initialList(identity), status: "unavailable" });
      }
    },
    [scope, identity],
  );

  const loadMore = useCallback(async () => {
    const offset = nextOffset.current;
    if (offset === null) {
      return;
    }
    const id = generation.current;
    const stale = () => id !== generation.current || current.current !== identity;
    setState((s) => (s.identity === identity ? { ...s, loadingMore: true } : s));
    try {
      const res = await fetchWithAuth(
        `${LIST_URL}?scope=${encodeURIComponent(scope)}&offset=${offset}`,
      );
      const body: unknown = await res.json().catch(() => null);
      if (stale()) {
        return;
      }
      const page = parsePage(res.status, body);
      setState((s) => {
        if (s.identity !== identity) {
          return s;
        }
        if (!page) {
          return { ...s, loadingMore: false, loadMoreFailed: true };
        }
        const seen = new Set(s.items.map((i) => i.useId));
        const added = page.items.filter((i) => !seen.has(i.useId));
        return {
          ...s,
          items: [...s.items, ...added],
          status: s.items.length + added.length === 0 ? "empty" : "ready",
          nextOffset: page.nextOffset,
          loadingMore: false,
          loadMoreFailed: false,
        };
      });
    } catch {
      if (stale()) {
        return;
      }
      setState((s) =>
        s.identity === identity ? { ...s, loadingMore: false, loadMoreFailed: true } : s,
      );
    }
  }, [scope, identity]);

  useEffect(() => {
    void load(false);
    const onChanged = () => void load(true);
    window.addEventListener(ROUTINES_CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener(ROUTINES_CHANGED_EVENT, onChanged);
      generation.current += 1;
    };
  }, [load]);

  const refetch = useCallback(() => load(false), [load]);
  // State stamped with another identity is never shown, even for the render before the effect.
  const shown = state.identity === identity ? state : initialList(identity);
  return {
    items: shown.items,
    status: shown.status,
    refetch,
    hasMore: shown.nextOffset !== null,
    loadMore,
    loadingMore: shown.loadingMore,
    loadMoreFailed: shown.loadMoreFailed,
  };
}

export type PersonalRoutineViewStatus =
  | "loading"
  | "ready"
  | "not_found"
  | "unauthenticated"
  | "forbidden"
  | "unavailable";

export function usePersonalRoutineView(useId: string | undefined): {
  view: PersonalRoutineView | null;
  status: PersonalRoutineViewStatus;
  refetch: () => Promise<void>;
} {
  const identity = useRoutinesIdentity();
  const [state, setState] = useState<{
    identity: string;
    useId: string | undefined;
    view: PersonalRoutineView | null;
    status: PersonalRoutineViewStatus;
  }>(() => ({ identity, useId, view: null, status: "loading" }));
  const generation = useRef(0);
  const current = useRef(identity);
  current.current = identity;

  const load = useCallback(async () => {
    const id = ++generation.current;
    const stale = () => id !== generation.current || current.current !== identity;
    const set = (view: PersonalRoutineView | null, status: PersonalRoutineViewStatus) =>
      setState({ identity, useId, view, status });
    set(null, "loading");
    if (!useId) {
      set(null, "not_found");
      return;
    }
    try {
      const res = await fetchWithAuth(`${LIST_URL}/u/${encodeURIComponent(useId)}`);
      const body: unknown = await res.json().catch(() => null);
      if (stale()) {
        return;
      }
      if (res.status === 404 || res.status === 400) {
        set(null, "not_found");
        return;
      }
      if (
        res.status !== 200 ||
        !isRecord(body) ||
        body.result !== "found" ||
        !isRecord(body.view)
      ) {
        set(
          null,
          res.status === 200 ? "unavailable" : (statusFor(res.status) as PersonalRoutineViewStatus),
        );
        return;
      }
      set(body.view as unknown as PersonalRoutineView, "ready");
    } catch {
      if (stale()) {
        return;
      }
      set(null, "unavailable");
    }
  }, [useId, identity]);

  useEffect(() => {
    void load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  const fresh = state.identity === identity && state.useId === useId;
  return {
    view: fresh ? state.view : null,
    status: fresh ? state.status : "loading",
    refetch: load,
  };
}

/**
 * Story 027.010: the owner's stored definition of one routine (`/routines/r/:routineId`), kept
 * exactly as Atlas sent it, for lossless inspection. Anything but a found definition with a
 * non-empty step list is `unavailable` (or `not_found`): never a partial graph.
 */
export interface PersonalRoutineDefinition {
  routineId: string;
  versionId: string;
  contentHash: string;
  definition: Record<string, unknown> & {
    steps: Array<Record<string, unknown>>;
  };
}

export type PersonalRoutineDefinitionStatus = PersonalRoutineViewStatus;

export function usePersonalRoutineDefinition(
  routineId: string | undefined,
  /** Bumped by the caller to read again (a reload after a version mismatch). */
  generation = 0,
): {
  routine: PersonalRoutineDefinition | null;
  status: PersonalRoutineDefinitionStatus;
} {
  const identity = useRoutinesIdentity();
  const [state, setState] = useState<{
    key: string;
    routine: PersonalRoutineDefinition | null;
    status: PersonalRoutineDefinitionStatus;
  }>({ key: "", routine: null, status: "loading" });
  const key = JSON.stringify([identity, routineId ?? "", generation]);

  useEffect(() => {
    let live = true;
    const set = (
      routine: PersonalRoutineDefinition | null,
      status: PersonalRoutineDefinitionStatus,
    ) => {
      if (live) {
        setState({ key, routine, status });
      }
    };
    if (!routineId) {
      set(null, "not_found");
      return;
    }
    void (async () => {
      try {
        const res = await fetchWithAuth(`${LIST_URL}/r/${encodeURIComponent(routineId)}`);
        const body: unknown = await res.json().catch(() => null);
        if (res.status === 404 || res.status === 400) {
          set(null, "not_found");
          return;
        }
        const routine = isRecord(body) && body.result === "found" ? body.routine : null;
        const definition = isRecord(routine) ? routine.definition : null;
        if (
          res.status !== 200 ||
          !isRecord(routine) ||
          routine.routineId !== routineId ||
          !isRecord(definition) ||
          !Array.isArray(definition.steps) ||
          definition.steps.length === 0 ||
          !definition.steps.every((s) => isRecord(s) && typeof s.id === "string")
        ) {
          set(
            null,
            res.status === 200
              ? "unavailable"
              : (statusFor(res.status) as PersonalRoutineDefinitionStatus),
          );
          return;
        }
        set(routine as unknown as PersonalRoutineDefinition, "ready");
      } catch {
        set(null, "unavailable");
      }
    })();
    return () => {
      live = false;
    };
  }, [key, routineId]);

  return state.key === key
    ? { routine: state.routine, status: state.status }
    : { routine: null, status: "loading" };
}
